import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Button } from '@ovComponents/2-component/button/button';
import { Checkbox } from '@ovComponents/2-component/checkbox/checkbox';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useThemeTokens } from '@providers/themeTokenProvider';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  LABEL_SX,
  MICRO_SX,
  SECONDARY_TEXT_SX,
  SECTION_HEADER_SX,
  TABULAR_SX,
  TRUNCATE_SX,
} from '../styles';
import type { ModelAggregation, PortfolioReport } from '../types';
import {
  calculateDrift,
  formatMoney,
  getPortfolioName,
  getStatusCode,
} from '../utils';

const driftColorPct = (
  sys: { color: Record<string, string> } | any,
  pct: number,
) => (pct > 10 ? sys.color.negative : pct > 5 ? sys.color.warning : undefined);

type ModelPortfolioViewProps = {
  models: ModelAggregation[];
  searchTerm: string;
  onRebalanceSelected?: (portfolios: PortfolioReport[]) => void;
  onSelectPortfolio?: (portfolioId: string) => void;
};

const STATUS_LABELS: Record<string, string> = {
  LIQ: 'Liquidating',
  XFER: 'Transfer',
  DRIFT: 'Drifted',
  PEND: 'Pending',
  OK: 'On Track',
};

const ModelRow = ({
  model,
  isSelected,
  onToggle,
  expanded,
  onToggleExpand,
  sys,
  onSelectPortfolio,
}: {
  model: ModelAggregation;
  isSelected: boolean;
  onToggle: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  sys: { color: Record<string, string> } | any;
  onSelectPortfolio?: (portfolioId: string) => void;
}) => {
  const [showEmpty, setShowEmpty] = useState(false);
  const [sortCol, setSortCol] = useState<
    'value' | 'cash' | 'drift' | 'status' | 'trades'
  >('value');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const handleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('desc');
    }
  };
  const cashPct =
    model.totalMarketValueCents > 0
      ? (model.totalCashCents / model.totalMarketValueCents) * 100
      : 0;
  const expandedStats = [
    { label: 'Portfolios', value: `${model.portfolioCount}` },
    { label: 'Total AUM', value: formatMoney(model.totalMarketValueCents) },
    { label: 'Total Cash', value: formatMoney(model.totalCashCents) },
    {
      label: 'Cash %',
      value: `${cashPct.toFixed(1)}%`,
      color:
        cashPct > 10
          ? sys.color.warning
          : cashPct > 5
            ? sys.color.onSurfaceVariant
            : undefined,
    },
    {
      label: 'Avg Drift',
      value: `${model.avgDriftPct.toFixed(1)}%`,
      color: driftColorPct(sys, model.avgDriftPct),
    },
    {
      label: 'Need Action',
      value: `${model.needsRebalanceCount}`,
      color: model.needsRebalanceCount > 0 ? sys.color.warning : undefined,
    },
  ];
  const headers: { label: string; sortKey?: typeof sortCol }[] = [
    { label: 'Portfolio' },
    { label: 'Mkt Value', sortKey: 'value' },
    { label: 'Cash', sortKey: 'cash' },
    { label: 'Drift', sortKey: 'drift' },
    { label: 'Status', sortKey: 'status' },
    { label: 'Trades', sortKey: 'trades' },
  ];

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          '&:hover': { bgcolor: sys.color.neutralHover },
          bgcolor: isSelected ? sys.color.primaryVariant : undefined,
        }}
        onClick={onToggleExpand}
      >
        <TableCell>
          <Box
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            display='flex'
            alignItems='center'
            justifyContent='center'
          >
            <Checkbox
              checked={isSelected}
              onChange={onToggle}
              customStyle={{ transform: 'scale(0.8)' }}
            />
          </Box>
        </TableCell>
        <TableCell>
          <IconButton
            size='small'
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </TableCell>
        <TableCell bold>{model.modelName}</TableCell>
        <TableCell center bold>
          {model.portfolioCount}
        </TableCell>
        <TableCell right number>
          {formatMoney(model.totalMarketValueCents)}
        </TableCell>
        <TableCell
          right
          number
          sx={{ color: driftColorPct(sys, model.avgDriftPct) }}
        >
          {model.avgDriftPct.toFixed(1)}%
        </TableCell>
        <TableCell center>
          {model.needsRebalanceCount > 0 ? (
            <Badge label={`${model.needsRebalanceCount}`} color='warning' />
          ) : (
            '—'
          )}
        </TableCell>
        <TableCell center bold>
          {model.pendingTradesCount > 0 ? model.pendingTradesCount : '—'}
        </TableCell>
        <TableCell right>{model.currencies.join(', ')}</TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0, borderBottom: 'none' }}>
            <Box
              sx={{
                bgcolor: sys.color.surface,
                borderTop: '2px solid',
                borderColor: sys.color.primary,
                borderBottom: '1px solid',
                borderBottomColor: sys.color.outlineVariant,
              }}
            >
              <Box
                display='flex'
                gap={0}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: sys.color.outlineVariant,
                }}
              >
                {expandedStats.map((stat) => (
                  <Box
                    key={stat.label}
                    flex={1}
                    py={0.75}
                    px={1.5}
                    sx={{
                      borderRight: '1px solid',
                      borderColor: sys.color.outlineVariant,
                      '&:last-child': { borderRight: 'none' },
                    }}
                  >
                    <Typography
                      variant='labelSmall'
                      sx={{ ...SECTION_HEADER_SX }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant='bodySmall'
                      sx={{
                        ...BOLD_SX,
                        color: stat.color || sys.color.onSurface,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                <Box
                  display='grid'
                  gridTemplateColumns='1fr 100px 90px 70px 72px 60px'
                  gap={0}
                  py={0.5}
                  px={1.5}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: sys.color.outlineVariant,
                    bgcolor: sys.color.neutralHover,
                  }}
                >
                  {headers.map((h) => (
                    <Typography
                      key={h.label}
                      variant='labelSmall'
                      onClick={
                        h.sortKey ? () => handleSort(h.sortKey!) : undefined
                      }
                      sx={{
                        ...SECTION_HEADER_SX,
                        textAlign: h.label === 'Portfolio' ? 'left' : 'right',
                        cursor: h.sortKey ? 'pointer' : undefined,
                        userSelect: h.sortKey ? 'none' : undefined,
                        '&:hover': h.sortKey
                          ? { color: sys.color.onSurface }
                          : undefined,
                      }}
                    >
                      {h.label}
                      {h.sortKey && sortCol === h.sortKey && (
                        <Box component='span' sx={{ ...MICRO_SX, ml: 0.3 }}>
                          {sortDir === 'asc' ? '▲' : '▼'}
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Box>

                {(() => {
                  const getSortVal = (p: PortfolioReport): number => {
                    if (sortCol === 'value') return p.marketValueCents || 0;
                    if (sortCol === 'cash') return p.cashCents || 0;
                    if (sortCol === 'drift') return calculateDrift(p) ?? 0;
                    if (sortCol === 'trades')
                      return p.pendingSubTradeRequests || 0;
                    if (sortCol === 'status') {
                      const s = getStatusCode(p);
                      const order: Record<string, number> = {
                        LIQ: 4,
                        DRIFT: 3,
                        XFER: 2,
                        PEND: 1,
                        OK: 0,
                      };
                      return order[s] ?? 0;
                    }
                    return p.marketValueCents || 0;
                  };
                  const mult = sortDir === 'asc' ? 1 : -1;
                  const allSorted = [...model.portfolios].sort(
                    (a, b) => mult * (getSortVal(a) - getSortVal(b)),
                  );
                  const withValue = allSorted.filter(
                    (p) =>
                      (p.marketValueCents || 0) > 0 || (p.cashCents || 0) > 0,
                  );
                  const zeroRows = allSorted.filter(
                    (p) =>
                      (p.marketValueCents || 0) === 0 &&
                      (p.cashCents || 0) === 0,
                  );
                  const display = showEmpty
                    ? [...withValue, ...zeroRows]
                    : withValue;
                  return (
                    <>
                      {display.map((p) => {
                        const drift = calculateDrift(p);
                        const status = getStatusCode(p);
                        const statusLabel = STATUS_LABELS[status] || status;
                        const isZero =
                          (p.marketValueCents || 0) === 0 &&
                          (p.cashCents || 0) === 0;
                        const driftAbs = drift !== null ? Math.abs(drift) : 0;
                        return (
                          <Box
                            key={p.id}
                            display='grid'
                            gridTemplateColumns='1fr 100px 90px 70px 72px 60px'
                            gap={0}
                            py={0.4}
                            px={1.5}
                            sx={{
                              borderBottom: '1px solid',
                              borderColor: sys.color.outlineVariant,
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: sys.color.neutralHover },
                              opacity: isZero ? 0.4 : 1,
                            }}
                          >
                            <Box overflow='hidden'>
                              <Typography
                                variant='bodySmall'
                                sx={{
                                  fontWeight: 600,
                                  ...TRUNCATE_SX,
                                  ...(onSelectPortfolio && {
                                    cursor: 'pointer',
                                    '&:hover': {
                                      color: sys.color.primary,
                                      textDecoration: 'underline',
                                    },
                                  }),
                                }}
                                onClick={
                                  onSelectPortfolio
                                    ? () => onSelectPortfolio(p.id)
                                    : undefined
                                }
                              >
                                {getPortfolioName(p)}
                              </Typography>
                              <Typography
                                variant='labelSmall'
                                sx={{ ...SECONDARY_TEXT_SX }}
                              >
                                {p.modelPortfolioType}
                              </Typography>
                            </Box>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                textAlign: 'right',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {formatMoney(p.marketValueCents)}
                            </Typography>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...TABULAR_SX,
                                textAlign: 'right',
                                color: sys.color.onSurfaceVariant,
                              }}
                            >
                              {formatMoney(p.cashCents)}
                            </Typography>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                ...TABULAR_SX,
                                textAlign: 'right',
                                fontWeight: driftAbs > 3 ? 700 : 400,
                                color:
                                  drift !== null
                                    ? (driftColorPct(sys, driftAbs) ??
                                      sys.color.onSurface)
                                    : sys.color.onSurface,
                              }}
                            >
                              {drift !== null ? `${drift.toFixed(1)}%` : '—'}
                            </Typography>
                            <Box display='flex' justifyContent='flex-end'>
                              <Badge
                                label={statusLabel}
                                color={
                                  status === 'OK'
                                    ? 'positive'
                                    : status === 'PEND'
                                      ? 'neutral'
                                      : 'warning'
                                }
                              />
                            </Box>
                            <Typography
                              variant='bodySmall'
                              sx={{
                                textAlign: 'right',
                                fontWeight:
                                  p.pendingSubTradeRequests > 0 ? 600 : 400,
                                color:
                                  p.pendingSubTradeRequests > 0
                                    ? sys.color.onSurface
                                    : sys.color.onDisabled,
                              }}
                            >
                              {p.pendingSubTradeRequests || '—'}
                            </Typography>
                          </Box>
                        );
                      })}
                      {zeroRows.length > 0 && (
                        <Box
                          display='flex'
                          justifyContent='center'
                          py={0.5}
                          sx={{
                            borderTop: '1px solid',
                            borderColor: sys.color.outlineVariant,
                          }}
                        >
                          <Typography
                            variant='labelSmall'
                            onClick={() => setShowEmpty((v) => !v)}
                            sx={{
                              ...LABEL_SX,
                              cursor: 'pointer',
                              color: sys.color.primary,
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {showEmpty
                              ? `Hide ${zeroRows.length} empty`
                              : `Show all (${zeroRows.length} with $0)`}
                          </Typography>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const ModelPortfolioView = ({
  models,
  searchTerm,
  onRebalanceSelected,
  onSelectPortfolio,
}: ModelPortfolioViewProps) => {
  const { sys } = useThemeTokens();
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());

  const toggleSet = useCallback(
    (setter: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) => {
      setter((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [],
  );

  const handleToggleModel = useCallback(
    (modelId: string) => toggleSet(setSelectedModels, modelId),
    [toggleSet],
  );
  const handleToggleExpand = useCallback(
    (modelId: string) => toggleSet(setExpandedModels, modelId),
    [toggleSet],
  );

  const filteredModels = useMemo(() => {
    if (!searchTerm) return models;
    const lower = searchTerm.toLowerCase();
    return models.filter((m) => m.modelName.toLowerCase().includes(lower));
  }, [models, searchTerm]);

  const allSelected =
    filteredModels.length > 0 &&
    filteredModels.every((m) => selectedModels.has(m.modelId));
  const someSelected = filteredModels.some((m) =>
    selectedModels.has(m.modelId),
  );

  const handleSelectAll = useCallback(() => {
    setSelectedModels((prev) =>
      prev.size === filteredModels.length
        ? new Set()
        : new Set(filteredModels.map((m) => m.modelId)),
    );
  }, [filteredModels]);

  const handleClearSelection = useCallback(
    () => setSelectedModels(new Set()),
    [],
  );

  const selectedPortfolios = useMemo(() => {
    const portfolios: PortfolioReport[] = [];
    filteredModels.forEach((m) => {
      if (selectedModels.has(m.modelId)) portfolios.push(...m.portfolios);
    });
    return portfolios;
  }, [selectedModels, filteredModels]);

  const selectedPortfolioCount = selectedPortfolios.length;

  return (
    <Box>
      {selectedModels.size > 0 && (
        <Box
          sx={{
            mb: 1,
            px: 2,
            py: 1,
            bgcolor: sys.color.primaryVariant,
            border: '1px solid',
            borderColor: sys.color.primary,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            <strong style={{ color: 'inherit' }}>{selectedModels.size}</strong>{' '}
            model{selectedModels.size !== 1 ? 's' : ''} selected ·{' '}
            <strong>{selectedPortfolioCount}</strong> portfolio
            {selectedPortfolioCount !== 1 ? 's' : ''}
          </Typography>
          <Box display='flex' gap={1} alignItems='center'>
            <Button
              label='Clear'
              variant='text'
              size='sm'
              onClick={handleClearSelection}
            />
            <Button
              label={`Rebalance ${selectedPortfolioCount} Portfolio${selectedPortfolioCount !== 1 ? 's' : ''}`}
              variant='filled'
              size='sm'
              onClick={() => onRebalanceSelected?.(selectedPortfolios)}
              disabled={selectedPortfolioCount === 0}
            />
          </Box>
        </Box>
      )}

      <Table sx={{ ...ALIGNED_TABLE_SX }}>
        <TableHead>
          <TableRow sx={{ bgcolor: sys.color.background }}>
            <TableHeadCell sx={{ width: 40 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleSelectAll}
                customStyle={{ transform: 'scale(0.8)' }}
              />
            </TableHeadCell>
            <TableHeadCell sx={{ width: 40 }} />
            <TableHeadCell>Model</TableHeadCell>
            <TableHeadCell center>Portfolios</TableHeadCell>
            <TableHeadCell right>Market Value</TableHeadCell>
            <TableHeadCell right>Avg Drift</TableHeadCell>
            <TableHeadCell center>Rebalance</TableHeadCell>
            <TableHeadCell center>Trades</TableHeadCell>
            <TableHeadCell right>Ccy</TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredModels.map((model) => (
            <ModelRow
              key={model.modelId}
              model={model}
              isSelected={selectedModels.has(model.modelId)}
              onToggle={() => handleToggleModel(model.modelId)}
              expanded={expandedModels.has(model.modelId)}
              onToggleExpand={() => handleToggleExpand(model.modelId)}
              sys={sys}
              onSelectPortfolio={onSelectPortfolio}
            />
          ))}
        </TableBody>
      </Table>

      {filteredModels.length === 0 && (
        <Box py={3} textAlign='center'>
          <Typography variant='bodySmall' sx={{ ...SECONDARY_TEXT_SX }}>
            {searchTerm
              ? 'No models match your search'
              : 'No model portfolio data available'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ModelPortfolioView;
